import type { JsonApiValidationError } from './useValidation.types';

/**
 * Converts a JSON:API pointer to a dotted field name for useValidation.
 *
 * - Standard attributes: `/data/attributes/...` → strip that prefix, then join segments with `.` (e.g. `email`,
 *   `price.min_value`, `terpenes.0.name`). Segment names are left unchanged (e.g. snake_case stays snake_case).
 * - Shorthand paths (no `data/attributes/`): join all segments with `.` (e.g. `/terpenes/0/percentage` →
 *   `terpenes.0.percentage`). Single-segment pointers (e.g. `/email`) still map to `email`.
 */
function pointerToFieldName(pointer: string): string {
  const normalized = pointer.replace(/^\//, '');
  const prefix = 'data/attributes/';

  if (normalized.startsWith(prefix)) {
    const pathWithinAttributes = normalized.slice(prefix.length);
    const segments = pathWithinAttributes.split('/').filter(Boolean);
    return segments.join('.');
  }

  const segments = normalized.split('/').filter(Boolean);
  return segments.length ? segments.join('.') : pointer;
}

/**
 * Parses an array of JSON:API error objects into a map of field names to error messages.
 * Uses `source.pointer` to determine the field (e.g. `/data/attributes/email` → `email`) and `detail` (or `title`) as the message.
 * If multiple errors reference the same field, the last one wins.
 */
export function parseJsonApiFieldErrors(errors: JsonApiValidationError[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const err of errors) {
    const pointer = err.source?.pointer;
    if (!pointer) {
      continue;
    }
    const message = err.detail || err.title || '';
    if (!message) {
      continue;
    }
    const fieldName = pointerToFieldName(pointer);
    map[fieldName] = message;
  }
  return map;
}
